import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { WORK_ORDER_SERVICE_TRANSITIONS } from '@lims/shared-types';
import type { WorkOrderServiceState } from '@lims/shared-types';
import {
  catalogueApi,
  organisationApi,
  settingsApi,
  workOrdersApi,
} from '../../api/endpoints.js';
import type { WorkOrderService } from '../../api/endpoints.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  ConfirmDialog,
  DataTable,
  Dialog,
  ErrorState,
  Form,
  LoadingState,
  Panel,
  SelectField,
  StatusBadge,
  TextAreaField,
  useToast,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDate, formatDateTime, formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { displayName } from '../clients/bilingual.js';
import { WorkOrderReportsSection } from '../reports/WorkOrderReportsSection.js';
import {
  ContextItem,
  Facts,
  RecordChain,
  RecordGrid,
  RecordHeader,
  RecordLink,
  RecordNumber,
} from '../clients/record-layout.js';
import { WorkOrderHistory } from './WorkOrderHistory.js';
import { WorkOrderLaboratory } from './WorkOrderLaboratory.js';
import { WorkOrderScopeDialog } from './WorkOrderScopeDialog.js';
import { toneForServiceState, toneForWorkOrderStatus } from './status.js';

/**
 * The work-order file.
 *
 * ===========================================================================
 * FOUR STATEMENTS THIS PAGE EXISTS TO MAKE HONESTLY.
 * ===========================================================================
 *
 *  1. PARTIAL IS NOT COMPLETE. Progress is a COUNT of services, never a
 *     percentage, and recording one service complete moves that service alone.
 *     The request's own status is derived by the backend from every service,
 *     so nothing on this page can mark the job finished while work remains.
 *
 *  2. REGISTERING A REQUEST IS NOT EVIDENCE A SAMPLE ARRIVED. A request with
 *     nothing started reads `awaiting_sample`, and the page says in words that
 *     no material has been booked in.
 *
 *  3. CANCELLING DELETES NOTHING. The Job Number, the dates, the prices and
 *     every completed service survive a cancellation exactly as they were, and
 *     the confirmation says so before anybody presses it.
 *
 *  4. WHAT THIS SYSTEM CANNOT YET KNOW, IT DOES NOT GUESS. Samples, laboratory
 *     assignments, reports and invoices are reported as not implemented, never
 *     as a count of zero — a zero would assert that none exists.
 *
 * NOTHING HERE ADVANCES BECAUSE A BUTTON WAS PRESSED. Every confirmation is
 * raised from the server's answer, and the page re-reads the record afterwards
 * rather than assuming what it now contains.
 */
export function WorkOrderFilePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [scopeOpen, setScopeOpen] = useState(false);
  const [stateTarget, setStateTarget] = useState<WorkOrderService | null>(null);
  const [nextState, setNextState] = useState<string | null>(null);
  const [stateNote, setStateNote] = useState('');
  const [stateReason, setStateReason] = useState('');

  const workOrder = useQuery({
    queryKey: ['work-order', id],
    queryFn: () => workOrdersApi.get(id as string),
  });

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: can(PERMISSIONS.SETTINGS_VIEW),
  });

  const catalogue = useQuery({
    queryKey: ['catalogue', 'selectable'],
    queryFn: () => catalogueApi.list({ pageSize: 100, includeInactive: false }),
    enabled: can(PERMISSIONS.WORK_ORDERS_UPDATE),
  });

  const departments = useQuery({
    queryKey: ['departments', 'active'],
    queryFn: () => organisationApi.listDepartments(false),
    enabled: can(PERMISSIONS.WORK_ORDERS_UPDATE),
  });

  const timezone = settings.data?.timezone;

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['work-order', id] });
    await queryClient.invalidateQueries({ queryKey: ['work-orders'] });
    await queryClient.invalidateQueries({ queryKey: ['audit', 'work-order'] });
  };

  /**
   * Record where ONE service has got to.
   *
   * It never marks the request complete. The response carries the request's
   * DERIVED status, and the page renders that rather than deciding for itself
   * what finishing this service meant for the job.
   */
  const recordState = useMutation({
    mutationFn: () =>
      workOrdersApi.setServiceState(
        id as string,
        (stateTarget as WorkOrderService).id,
        {
          state: nextState as string,
          ...(stateNote.trim() ? { note: stateNote.trim() } : {}),
          ...(stateReason.trim() ? { reason: stateReason.trim() } : {}),
        },
      ),
    onSuccess: async () => {
      await invalidate();
      setStateTarget(null);
      // Raised from the server's answer, never from the press of the button.
      show({ tone: 'success', message: t('workOrders.stateRecorded') });
    },
  });

  const cancel = useMutation({
    mutationFn: () =>
      workOrdersApi.cancel(id as string, { reason: cancelReason.trim() }),
    onSuccess: async () => {
      await invalidate();
      setCancelOpen(false);
      show({ tone: 'success', message: t('workOrders.cancelled') });
    },
  });

  if (workOrder.isLoading) return <LoadingState label={t('common.loading')} />;
  if (workOrder.error) {
    return (
      <ErrorState
        error={workOrder.error}
        onRetry={() => void workOrder.refetch()}
      />
    );
  }

  const record = workOrder.data;
  if (!record) return null;

  const money = (minor: number) =>
    formatMoney(minor, { locale, currency: record.currency });

  const stateFailure = (code: string) =>
    recordState.error instanceof ApiError &&
    recordState.error.details?.some((detail) => detail.code === code);

  const openStateDialog = (service: WorkOrderService) => {
    setStateTarget(service);
    setNextState(null);
    setStateNote('');
    setStateReason('');
    recordState.reset();
  };

  /**
   * Only the transitions a person may actually record.
   *
   * `complete` and `cancelled` are terminal: reopening finished work is not an
   * edit to this row, and quietly flipping a completed service back would
   * erase the fact that it had ever been finished.
   */
  const allowedStates = stateTarget
    ? WORK_ORDER_SERVICE_TRANSITIONS[
        stateTarget.state as WorkOrderServiceState
      ] ?? []
    : [];

  const columns: readonly DataColumn<WorkOrderService>[] = [
    {
      key: 'description',
      header: t('workOrders.service'),
      render: (service) => (
        <div className="min-w-0">
          <span className="block text-default-900">
            {displayName(service.description, locale) ??
              service.itemCode ??
              t('common.notRecorded')}
          </span>
          {service.referenceStandard ? (
            <span className="block text-caption text-default-600" dir="ltr">
              {service.referenceStandard}
            </span>
          ) : null}
          {service.department ? (
            <span className="block text-caption text-default-600">
              {service.department.name}
            </span>
          ) : null}
          {/*
            What the catalogue says about a specialised template — and what it
            says today, for every entry, is that there is none and the job runs
            on the general workflow (CLAUDE.md section 4).
          */}
          {service.usesGeneralWorkflowOnly ? (
            <span className="block text-caption text-default-600">
              {t('workOrders.generalWorkflowOnly')}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'quantity',
      header: t('workOrders.quantity'),
      align: 'end',
      render: (service) => (
        <span dir="ltr" className="measure">
          {service.quantity}
        </span>
      ),
    },
    {
      key: 'unitPrice',
      header: t('workOrders.unitPrice'),
      align: 'end',
      render: (service) => (
        <div className="text-end">
          <span className="measure block text-default-900" dir="ltr">
            {money(service.unitPriceMinor)}
          </span>
          {/*
            Where the price came from, beside the price. A snapshot carried
            from an accepted quotation and a price somebody overrode cannot be
            told apart from the number alone.
          */}
          <span className="text-caption text-default-600">
            {t(`priceSource.${service.priceSource}`)}
          </span>
        </div>
      ),
    },
    {
      key: 'total',
      header: t('workOrders.total'),
      align: 'end',
      render: (service) => (
        <span className="measure text-default-900" dir="ltr">
          {money(service.totalMinor)}
        </span>
      ),
    },
    {
      key: 'state',
      header: t('workOrders.serviceStateLabel'),
      render: (service) => (
        <div className="min-w-0">
          <StatusBadge
            label={t(`workOrderServiceState.${service.state}`)}
            tone={toneForServiceState(service.state)}
          />
          {/*
            When it happened, on the service that it happened to. Each of these
            was recorded by a person; none of them is inferred.
          */}
          {service.startedAt ? (
            <span className="mt-1 block text-caption text-default-600">
              {t('workOrders.startedAt')}:{' '}
              {formatDateTime(service.startedAt, {
                locale,
                ...(timezone ? { timezone } : {}),
              })}
            </span>
          ) : null}
          {service.completedAt ? (
            <span className="block text-caption text-default-600">
              {t('workOrders.completedAt')}:{' '}
              {formatDateTime(service.completedAt, {
                locale,
                ...(timezone ? { timezone } : {}),
              })}
            </span>
          ) : null}
          {service.cancelledAt ? (
            <span className="block text-caption text-default-600">
              {t('workOrders.serviceCancelledAt')}:{' '}
              {formatDateTime(service.cancelledAt, {
                locale,
                ...(timezone ? { timezone } : {}),
              })}
            </span>
          ) : null}
          {service.completionNote ? (
            <span className="block text-caption text-default-600">
              {service.completionNote}
            </span>
          ) : null}
          {service.cancellationReason ? (
            <span className="block text-caption text-default-600">
              {service.cancellationReason}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (service) =>
        can(PERMISSIONS.WORK_ORDERS_UPDATE) &&
        !record.isCancelled &&
        (WORK_ORDER_SERVICE_TRANSITIONS[
          service.state as WorkOrderServiceState
        ]?.length ?? 0) > 0 ? (
          <ActionButton
            priority="tertiary"
            size="sm"
            onPress={() => openStateDialog(service)}
          >
            {t('workOrders.recordState')}
          </ActionButton>
        ) : null,
    },
  ];

  const notRecorded = t('common.notRecorded');
  const when = (value: string) =>
    formatDateTime(value, {
      locale,
      ...(timezone ? { timezone } : {}),
    });
  const clientLabel = record.clientName ?? record.clientNumber ?? notRecorded;

  return (
    <>
      <RecordHeader
        number={record.workOrderNumber}
        badges={
          <>
            {/* DERIVED by the backend. Rendered, never recalculated here. */}
            <StatusBadge
              label={t(`status.workOrder.${record.status}`)}
              tone={toneForWorkOrderStatus(record.status)}
            />
            {record.isOverdue ? (
              <StatusBadge tone="warning" label={t('workOrders.overdueBadge')} />
            ) : null}
            <span className="text-caption text-default-600">
              {t(`workOrderPriority.${record.priority}`)}
            </span>
          </>
        }
        title={record.subject ?? record.clientName ?? record.workOrderNumber}
        context={
          <>
            <ContextItem label={t('workOrders.client')}>
              <RecordLink to={`/clients/${record.clientId}`}>{clientLabel}</RecordLink>
            </ContextItem>
            {record.quotationId && record.quotationNumber ? (
              <ContextItem label={t('workOrders.quotation')}>
                <RecordLink to={`/quotations/${record.quotationId}`} isNumber>
                  {record.quotationNumber}
                </RecordLink>
              </ContextItem>
            ) : null}
            <ContextItem label={t('workOrders.dueDate')}>
              {/* Not a blank, and not today: the laboratory promised nothing. */}
              {record.dueDate
                ? formatDate(record.dueDate, { locale })
                : t('workOrders.dueNotPromised')}
            </ContextItem>
          </>
        }
        breadcrumbs={[
          { label: t('nav.workOrders'), to: '/work-orders' },
          { label: record.workOrderNumber },
        ]}
        actions={
          <ActionBar>
            {can(PERMISSIONS.WORK_ORDERS_UPDATE) && !record.isCancelled ? (
              <ActionButton
                priority="tertiary"
                onPress={() => navigate(`/work-orders/${record.id}/edit`)}
              >
                {t('common.edit')}
              </ActionButton>
            ) : null}
            {can(PERMISSIONS.WORK_ORDERS_UPDATE) && record.acceptsScopeChange ? (
              <ActionButton
                priority="secondary"
                onPress={() => setScopeOpen(true)}
              >
                {t('workOrders.scopeChange')}
              </ActionButton>
            ) : null}
            {can(PERMISSIONS.WORK_ORDERS_CANCEL) && !record.isCancelled ? (
              <ActionButton
                priority="destructive"
                onPress={() => {
                  setCancelReason('');
                  cancel.reset();
                  setCancelOpen(true);
                }}
              >
                {t('workOrders.cancel')}
              </ActionButton>
            ) : null}
          </ActionBar>
        }
      />

      {record.isCancelled ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('workOrders.cancelledNotice')}
            description={record.cancellationReason ?? t('workOrders.cancelHint')}
          />
        </div>
      ) : null}

      {record.status === 'awaiting_sample' ? (
        <div className="mb-stack">
          <Alert
            tone="info"
            title={t('workOrders.awaitingSampleNotice')}
            description={t('workOrders.awaitingSampleHint')}
          />
        </div>
      ) : null}

      {record.status === 'partially_complete' ? (
        <div className="mb-stack">
          <Alert
            tone="info"
            title={t('workOrders.partialNotice')}
            description={t('workOrders.partialHint')}
          />
        </div>
      ) : null}

      {record.isOverdue ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('workOrders.overdueNotice')}
            description={t('workOrders.overdueHint')}
          />
        </div>
      ) : null}

      <RecordGrid
        side={
          <>
            <Panel title={t('common.details')}>
              <Facts
                items={[
                  {
                    label: t('workOrders.client'),
                    value: <RecordLink to={`/clients/${record.clientId}`}>{clientLabel}</RecordLink>,
                  },
                  {
                    label: t('workOrders.branch'),
                    value: record.branch ? record.branch.name : t('common.none'),
                  },
                  {
                    label: t('workOrders.quotation'),
                    value: record.quotationNumber ? (
                      <span dir="ltr" className="measure">
                        {record.quotationNumber}
                      </span>
                    ) : (
                      t('workOrders.noQuotation')
                    ),
                  },
                  {
                    label: t('workOrders.requestDate'),
                    value: formatDate(record.requestDate, { locale }),
                  },
                  {
                    label: t('workOrders.dueDate'),
                    value: record.dueDate
                      ? formatDate(record.dueDate, { locale })
                      : t('workOrders.dueNotPromised'),
                  },
                  {
                    label: t('workOrders.requestingPartyName'),
                    value: record.requestingParty.name ? (
                      <bdi>{record.requestingParty.name}</bdi>
                    ) : (
                      notRecorded
                    ),
                  },
                  {
                    label: t('workOrders.purchaseOrderNumber'),
                    value: record.purchaseOrderNumber ? (
                      <bdi>{record.purchaseOrderNumber}</bdi>
                    ) : (
                      notRecorded
                    ),
                  },
                  {
                    label: t('workOrders.clientReference'),
                    value: record.clientReference ? (
                      <bdi>{record.clientReference}</bdi>
                    ) : (
                      notRecorded
                    ),
                  },
                  {
                    label: t('workOrders.notes'),
                    value: record.notes ? (
                      <span className="whitespace-pre-line" dir="auto">
                        {record.notes}
                      </span>
                    ) : (
                      notRecorded
                    ),
                  },
                ]}
              />
            </Panel>

            {/*
              The chain this request sits in, as far as the file knows it. The
              samples, the work on them and the reports follow in the main
              column, each from the register that owns it.
            */}
            <Panel title={t('ordersRecord.chain')}>
              <RecordChain
                items={[
                  {
                    label: t('workOrders.client'),
                    value: (
                      <RecordLink to={`/clients/${record.clientId}`}>
                        {record.clientNumber ?? clientLabel}
                      </RecordLink>
                    ),
                  },
                  record.projectId
                    ? {
                        label: t('quotations.project'),
                        value: (
                          <RecordLink to={`/projects/${record.projectId}`}>
                            {t('projects.fileTitle')}
                          </RecordLink>
                        ),
                      }
                    : null,
                  record.quotationId && record.quotationNumber
                    ? {
                        label: t('workOrders.quotation'),
                        value: (
                          <RecordLink to={`/quotations/${record.quotationId}`} isNumber>
                            {record.quotationNumber}
                          </RecordLink>
                        ),
                      }
                    : null,
                  {
                    label: t('workOrders.number'),
                    value: <RecordNumber>{record.workOrderNumber}</RecordNumber>,
                  },
                ]}
              />
            </Panel>

            {/*
              The request's own recorded moments. Every one of them is a stored
              timestamp, not an estimate, and an absent one is stated as absent
              rather than filled in with today.
            */}
            <Panel
              title={t('workOrders.timelineTitle')}
              description={t('workOrders.timelineHint')}
            >
              <Facts
                items={[
                  { label: t('workOrders.raisedAt'), value: when(record.createdAt) },
                  { label: t('workOrders.updatedAt'), value: when(record.updatedAt) },
                  {
                    label: t('workOrders.scopeChangeCount'),
                    value: (
                      <span dir="ltr" className="measure">
                        {record.scopeChangeCount}
                      </span>
                    ),
                  },
                  {
                    label: t('workOrders.lastScopeChange'),
                    value: record.lastScopeChangeAt
                      ? when(record.lastScopeChangeAt)
                      : t('workOrders.neverChanged'),
                  },
                  {
                    label: t('workOrders.cancelledAt'),
                    value: record.cancelledAt ? when(record.cancelledAt) : t('common.no'),
                  },
                ]}
              />
            </Panel>

            {/*
              What this system cannot yet know, it does not guess. This is not a
              zero: a zero would assert that no invoice exists.
            */}
            <SectionCard
              label={t('workOrders.sectionInvoices')}
              state={record.sections.invoices?.state ?? 'not_implemented'}
              {...(record.sections.invoices?.plannedPhase !== undefined
                ? { plannedPhase: record.sections.invoices.plannedPhase }
                : {})}
            />

            <WorkOrderHistory
              workOrderId={record.id}
              {...(timezone ? { timezone } : {})}
            />
          </>
        }
      >
        <Panel
          title={t('workOrders.servicesTitle')}
          description={
            record.quotationNumber
              ? t('workOrders.snapshotHint')
              : t('workOrders.servicesHint')
          }
        >
          {/*
            Progress as COUNTS, never a percentage, which invites rounding to
            "done" — said once, above the services it counts.
          */}
          <section aria-label={t('workOrders.progressTitle')} className="mb-stack">
            <p className="text-caption font-semibold text-default-800">
              {t('workOrders.progressTitle')}
            </p>
            <p className="mb-2 text-caption text-default-600">{t('workOrders.progressHint')}</p>
            <dl className="grid grid-cols-2 gap-x-gutter gap-y-2 rounded-medium bg-default-50 px-3 py-2 sm:grid-cols-5">
              {(
                [
                  ['workOrders.countTotal', record.progress.total],
                  ['workOrders.countRequested', record.progress.requested],
                  ['workOrders.countInProgress', record.progress.inProgress],
                  ['workOrders.countCompleted', record.progress.completed],
                  ['workOrders.countCancelled', record.progress.cancelled],
                ] as const
              ).map(([key, count]) => (
                <div key={key} className="min-w-0">
                  <dt className="text-caption text-default-600">{t(key)}</dt>
                  <dd className="measure text-body font-semibold text-default-900" dir="ltr">
                    {count}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          <DataTable
            label={t('workOrders.servicesTitle')}
            columns={columns}
            rows={record.services}
            rowKey={(service) => service.id}
            emptyContent={t('workOrders.noServices')}
          />

          {/* The totals, at the trailing edge under the services they sum. */}
          <div className="mt-stack flex justify-end border-t border-default-200 pt-stack">
            <dl className="grid w-full max-w-sm grid-cols-[minmax(0,1fr)_auto] gap-x-gutter gap-y-1 text-body">
              {(
                [
                  [t('workOrders.subtotal'), record.totals.subtotalMinor],
                  [t('workOrders.discount'), record.totals.discountMinor],
                  [t('workOrders.tax'), record.totals.taxMinor],
                ] as const
              ).map(([label, minor]) => (
                <div key={label} className="contents">
                  <dt className="text-default-600">{label}</dt>
                  <dd className="measure text-end text-default-900" dir="ltr">
                    {money(minor)}
                  </dd>
                </div>
              ))}
              <div className="contents">
                <dt className="border-t border-default-200 pt-1 font-semibold text-default-900">
                  {t('workOrders.total')}
                </dt>
                <dd
                  className="measure border-t border-default-200 pt-1 text-end font-semibold text-default-900"
                  dir="ltr"
                >
                  {money(record.totals.totalMinor)}
                </dd>
              </div>
            </dl>
          </div>
        </Panel>

        {/*
          The material that arrived against this request, and the laboratory
          work on it. Read-only summaries over the sample register and the work
          list, each permission-gated and each linking to the register that
          owns the record. They replaced two "not implemented" cards, which had
          stopped being true once Phases 7 and 8 shipped (limitation 41).
        */}
        <WorkOrderLaboratory workOrderId={record.id} />

        {/*
          Phase 10: this request's reports, read-only, from the report register
          (gated on `reports.view`; nothing at all is shown without it).
        */}
        <WorkOrderReportsSection workOrderId={record.id} />
      </RecordGrid>

      {/* Record where ONE service got to. Never the request as a whole. */}
      <Dialog
        isOpen={stateTarget !== null}
        onClose={() => setStateTarget(null)}
        title={t('workOrders.recordStateTitle')}
      >
        <div className="mb-stack">
          <Alert tone="info" title={t('workOrders.recordStateHonesty')} />
        </div>

        {stateFailure('transition_not_allowed') ? (
          <div className="mb-stack">
            <Alert tone="warning" title={t('workOrders.transitionNotAllowed')} />
          </div>
        ) : null}
        {stateFailure('reason_required') ? (
          <div className="mb-stack">
            <Alert tone="warning" title={t('workOrders.stateReasonRequired')} />
          </div>
        ) : null}

        <Form
          onSubmit={() => recordState.mutate()}
          isSubmitting={recordState.isPending}
          actions={
            <ActionBar>
              <ActionButton
                priority="tertiary"
                onPress={() => setStateTarget(null)}
              >
                {t('common.cancel')}
              </ActionButton>
              <ActionButton
                priority="primary"
                type="submit"
                isLoading={recordState.isPending}
                isDisabled={nextState === null}
              >
                {t('workOrders.recordState')}
              </ActionButton>
            </ActionBar>
          }
        >
          <SelectField
            label={t('workOrders.serviceStateLabel')}
            placeholder={t('common.notSet')}
            value={nextState}
            onChange={setNextState}
            isRequired
            options={allowedStates.map((state) => ({
              value: state,
              label: t(`workOrderServiceState.${state}`),
            }))}
          />
          <TextAreaField
            label={t('workOrders.stateNote')}
            value={stateNote}
            onChange={setStateNote}
            rows={2}
          />
          {/*
            Required when cancelling one service, for the same reason as
            cancelling a whole request: an unexplained stop is one nobody can
            account for afterwards.
          */}
          {nextState === 'cancelled' ? (
            <TextAreaField
              label={t('common.reason')}
              description={t('workOrders.stateReasonHint')}
              value={stateReason}
              onChange={setStateReason}
              rows={2}
              isRequired
            />
          ) : null}

          {recordState.error ? <ErrorState error={recordState.error} /> : null}
        </Form>
      </Dialog>

      <WorkOrderScopeDialog
        workOrder={record}
        isOpen={scopeOpen}
        onClose={() => setScopeOpen(false)}
        onSaved={async () => {
          await invalidate();
          show({ tone: 'success', message: t('workOrders.scopeChanged') });
        }}
        catalogue={catalogue.data?.data ?? []}
        departments={departments.data?.data ?? []}
        canOverride={can(PERMISSIONS.PRICING_OVERRIDE)}
      />

      {/*
        The confirmation states what cancelling ACTUALLY does, which is stop
        unexecuted work — not remove anything. The reason is required.
      */}
      <ConfirmDialog
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={t('workOrders.cancelTitle')}
        message={t('workOrders.cancelMessage')}
        confirmLabel={t('workOrders.cancel')}
        isPending={cancel.isPending}
        onConfirm={async () => {
          // Awaited so the dialog closes on the SERVER's answer, never on the
          // press of the button.
          await cancel.mutateAsync();
        }}
      >
        <div className="flex flex-col gap-stack">
          <p className="text-caption text-default-600">
            {t('workOrders.cancelHint')}
          </p>
          <TextAreaField
            label={t('common.reason')}
            description={t('workOrders.cancelReasonHint')}
            value={cancelReason}
            onChange={setCancelReason}
            rows={3}
            isRequired
          />
          {cancel.error ? <ErrorState error={cancel.error} /> : null}
        </div>
      </ConfirmDialog>
    </>
  );
}

/**
 * One section of the file that a later phase owns.
 *
 * The distinction it exists to draw: `0` and "this system cannot say" look
 * identical on a screen and mean entirely different things.
 */
function SectionCard({
  label,
  state,
  plannedPhase,
}: {
  readonly label: string;
  readonly state: string;
  readonly plannedPhase?: number;
}) {
  const { t } = useTranslation();

  return (
    <div className="min-w-0 rounded-large border border-default-200 bg-white p-4">
      <p className="text-caption text-default-600">{label}</p>
      {state === 'not_implemented' ? (
        <>
          {/* No number at all. Not a zero, not a dash standing in for one. */}
          <p className="mt-1 text-body font-medium text-default-700">
            {t('workOrders.notImplemented')}
          </p>
          <p className="text-caption text-default-600">
            {t('workOrders.notImplementedHint')}
          </p>
          {plannedPhase ? (
            <p className="mt-1 text-caption text-default-600">
              {t('workOrders.plannedPhase', { phase: plannedPhase })}
            </p>
          ) : null}
        </>
      ) : (
        <p className="mt-1 text-body text-default-700">{state}</p>
      )}
    </div>
  );
}
